import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import { ContainerBox } from './styles';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { abyssColors } from '@/theme/colors';
import { useFeatureFlag } from '@/hooks';
import { useSetAtom } from 'jotai';
import { AllStructuresForHomePageQuery, StructureStatus } from '@/__generated__/graphql';
import { FALLBACK_INSPECTION_IMAGE } from '@/constants';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { getStructureRedirectPath } from '@/utils/structureRedirectPath';
import * as state from '@/state/atoms';
import { useRouter } from '@/hooks';
import { Image } from '../Analysis/shared/Image';

type StatusType = 'active' | 'inActive';

type Props = {
  deploymentStatus: StatusType;
  structure: AllStructuresForHomePageQuery['allStructures'][0] | undefined;
};

export const DeploymentCard = ({ deploymentStatus, structure }: Props) => {
  const isBetaUser = useFeatureFlag('beta-user');
  const setSelectedStructureId = useSetAtom(state.selectedStructureId);
  const router = useRouter();

  const handlePlatformChange = useCallback(
    (id: string, status: StructureStatus) => {
      setSelectedStructureId(id);
      const pathname = getStructureRedirectPath({ id, status }, isBetaUser);
      router.push({
        pathname,
      });
    },
    [isBetaUser, router, setSelectedStructureId]
  );

  return (
    <Grid size={{ md: deploymentStatus === 'active' ? 6 : 3 }}>
      {structure && (
        <ContainerBox
          direction="row"
          bgcolor={
            deploymentStatus === 'active' ? abyssColors.primary[900] : abyssColors.primary[400]
          }
          sx={{
            cursor: 'pointer',
            ':hover': {
              backgroundColor:
                deploymentStatus === 'active' ? abyssColors.primary[800] : abyssColors.primary[400],
            },
          }}
          onClick={() => handlePlatformChange(structure?.id, structure?.status)}
        >
          <Stack direction="row" alignItems="center" gap="2.4rem" width="calc(100% - 3.2rem)">
            {deploymentStatus === 'active' && (
              <Image
                src={
                  structure?.coverImagePath
                    ? getCloudfrontUrl(structure?.coverImagePath)
                    : FALLBACK_INSPECTION_IMAGE
                }
                alt={structure?.name || ''}
                imgSx={{
                  width: '26rem',
                  height: '17rem',
                  objectFit: 'cover',
                  overflow: 'hidden',
                  transition: 'opacity 0.3s ease-in-out',
                }}
                sx={{
                  width: '26rem',
                  height: '17rem',
                }}
              />
            )}
            <Stack
              sx={{
                overflow: 'hidden',
                flex: 1,
              }}
            >
              <Typography
                variant="h3"
                fontWeight={500}
                color={abyssColors.neutral[50]}
                style={{
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                {structure?.name}
              </Typography>

              <Stack direction="row" alignItems="center" gap={2} mt={4}>
                <Typography variant="body2" fontWeight={500} color={abyssColors.primary[50]}>
                  Scan Date: {structure.scanDate ?? 'N/A'}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <ArrowForwardIosIcon color="secondary" />
        </ContainerBox>
      )}
    </Grid>
  );
};
